import { FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import { USERS_ENDPOINT } from "Constants";
import { baseApi as api, listenToUserUpdates } from "Services";
import { RootState } from "Store/store";
import { UserUpdatesMessage } from "Types";
import {
  DeleteUserRequest,
  GetUserRequest,
  GetUserResponse,
  GetUsersListRequest,
  GetUsersListResponse,
  RegisterUserRequest,
  RegisterUserResendRequest,
  RegisterUserResendResponse,
  RegisterUserResponse,
  SetUserGroupsRequest,
  UpdateUserGroupRoleRequest,
  UpdateUserRequest,
  UpdateUserResponse,
} from "../types";
import { updateUsersListCache } from "./websocketUpdates";

const usersApi = api.injectEndpoints({
  endpoints: (build) => ({
    getUsersList: build.query<GetUsersListResponse, GetUsersListRequest>({
      query: (queryArg) => ({
        url: `${USERS_ENDPOINT}/users`,
        params: {
          organization_id: queryArg.organization,
          group_id: queryArg.group,
          role: queryArg.role,
          email: queryArg.email,
          sort_by: queryArg.sortBy,
          limit: queryArg.limit,
          offset: queryArg.offset,
        },
      }),
      // TODO: This is currently using whoami updates to handle change to user role
      // It needs to be reworked once backend implements everything from specification:
      // https://alt-confluence.rl.lan/display/SwA/Streaming+updates+endpoint+prioritization
      async onCacheEntryAdded(
        arg,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved, getState },
      ) {
        const listener = (event: MessageEvent) => {
          const state = getState() as RootState;
          const data = JSON.parse(event.data) as UserUpdatesMessage;

          if (data.entity === "current_user") {
            updateCachedData((draft) => {
              return updateUsersListCache(arg, draft, data, state);
            });
          }
        };

        listenToUserUpdates(cacheDataLoaded, cacheEntryRemoved, listener);
      },
      providesTags: ["Users"],
    }),
    getUser: build.query<GetUserResponse, GetUserRequest>({
      query: (queryArg) => ({
        url: `${USERS_ENDPOINT}/users/${queryArg.userId}`,
      }),
      providesTags: ["Users"],
    }),
    deleteUser: build.mutation<void, DeleteUserRequest>({
      query: (queryArg) => ({
        url: `${USERS_ENDPOINT}/users/${queryArg.userId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Users"],
    }),
    setUserGroups: build.mutation<void, SetUserGroupsRequest>({
      query: (queryArg) => ({
        url: `${USERS_ENDPOINT}/users/${queryArg.userId}/set-groups`,
        method: "PATCH",
        body: queryArg.body,
      }),
      invalidatesTags: ["Users"],
    }),
    updateUserGroupRole: build.mutation<void, UpdateUserGroupRoleRequest>({
      query: (queryArg) => ({
        url: `${USERS_ENDPOINT}/users/${queryArg.userId}/modify-group-role`,
        method: "PUT",
        body: queryArg.body,
      }),
      invalidatesTags: ["Users"],
    }),

    // custom query that wraps two actions:
    // - update user groups
    // - update user details
    // and will only invalidate tags at the end of those two actions
    // automatically handles errors in a notification
    updateUserAndGroups: build.mutation<
      UpdateUserResponse | FetchBaseQueryError,
      UpdateUserRequest & SetUserGroupsRequest
    >({
      async queryFn(queryArg, _queryApi, _extraOptions, fetchWithBaseQuery) {
        const { is_active, first_name, last_name, email, org_role } =
          queryArg.body;
        const { group_ids: group_ids } = queryArg.body;

        const updateGroups = await fetchWithBaseQuery({
          url: `${USERS_ENDPOINT}/users/${queryArg.userId}/set-groups`,
          method: "PATCH",
          body: { group_ids },
        });

        if (updateGroups.error) return { error: updateGroups.error };

        const updateUser = await fetchWithBaseQuery({
          url: `${USERS_ENDPOINT}/users/${queryArg.userId}`,
          method: "PATCH",
          body: { is_active, first_name, last_name, email, org_role },
        });

        if (updateUser.error) return { error: updateUser.error };

        return { data: updateUser.data as UpdateUserResponse };
      },
      invalidatesTags: ["Users"],
    }),
    registerUser: build.mutation<RegisterUserResponse, RegisterUserRequest>({
      query: (queryArg) => ({
        url: `${USERS_ENDPOINT}/users/register`,
        method: "POST",
        body: queryArg.body,
      }),
      invalidatesTags: ["Users"],
    }),
    resendRegisterUserEmail: build.mutation<
      RegisterUserResendResponse,
      RegisterUserResendRequest
    >({
      query: (queryArg) => ({
        url: `${USERS_ENDPOINT}/users/register/${queryArg.email}/resend`,
        method: "POST",
      }),
      invalidatesTags: [],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetUsersListQuery,
  useDeleteUserMutation,
  useUpdateUserGroupRoleMutation,
  useUpdateUserAndGroupsMutation,
  useRegisterUserMutation,
  useResendRegisterUserEmailMutation,
} = usersApi;
